import React, { useEffect, useRef, useState, useCallback } from 'react'
import { motion } from 'framer-motion'
import { useAppStore } from '@/hooks/useAppStore'
import { Topbar } from '@/components/shared/Topbar'
import { Atmosphere } from '@/components/shared/Atmosphere'
import {
  loadGoogleMaps,
  initMap,
  placeMarkers,
  drawRoute,
  fitBounds,
  resolveStopCoordinates,
} from '@/services/maps'
import { openDeal, getNetworkLabel, NETWORK_COLORS } from '@/services/affiliates'
import { fetchImages, type Img } from '@/services/images'
import { DNA_PROFILES } from '@/data/destinations'
import type { Stop, Deal, Destination } from '@/types'

// ─── Fallback canvas map (used when Maps API key is missing) ──────────────────

function FallbackMap({ stopCount }: { stopCount: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const W = canvas.width
    const H = canvas.height
    ctx.clearRect(0, 0, W, H)

    const bg = ctx.createLinearGradient(0, 0, W, H)
    bg.addColorStop(0, '#241a42')
    bg.addColorStop(1, '#3a2255')
    ctx.fillStyle = bg
    ctx.fillRect(0, 0, W, H)

    ctx.strokeStyle = 'rgba(255,214,140,0.08)'
    ctx.lineWidth = 1
    for (let x = 0; x < W; x += 35) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke() }
    for (let y = 0; y < H; y += 35) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke() }

    const pts = [[55, 185], [125, 120], [205, 85], [290, 135], [370, 180]].slice(0, stopCount)

    ctx.beginPath()
    ctx.strokeStyle = 'rgba(255,149,0,0.65)'
    ctx.lineWidth = 2.5
    ctx.setLineDash([7, 5])
    pts.forEach(([x, y], i) => i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y))
    ctx.stroke()
    ctx.setLineDash([])

    ctx.fillStyle = 'rgba(61,219,217,0.6)'
    pts.slice(0, -1).forEach(([x1, y1], i) => {
      const [x2, y2] = pts[i + 1]
      ctx.beginPath()
      ctx.arc((x1 + x2) / 2, (y1 + y2) / 2, 3, 0, Math.PI * 2)
      ctx.fill()
    })

    // Teardrop pin shape per stop
    pts.forEach(([x, y], i) => {
      ctx.save()
      ctx.translate(x, y - 14)
      ctx.beginPath()
      ctx.arc(0, 0, 11, Math.PI, 0, false)
      ctx.lineTo(0, 22)
      ctx.closePath()
      ctx.fillStyle = '#FF9500'
      ctx.shadowColor = 'rgba(255,149,0,0.6)'
      ctx.shadowBlur = 8
      ctx.fill()
      ctx.shadowBlur = 0
      ctx.fillStyle = '#fff'
      ctx.font = 'bold 11px Nunito, sans-serif'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(String(i + 1), 0, -1)
      ctx.restore()
    })

    // Watermark hint
    ctx.fillStyle = 'rgba(255,247,236,0.4)'
    ctx.font = '10px Nunito, sans-serif'
    ctx.textAlign = 'center'
    ctx.fillText('Add VITE_GOOGLE_MAPS_API_KEY to enable live map', W / 2, H - 10)
  }, [stopCount])

  return <canvas ref={canvasRef} width={430} height={250} className="w-full h-full" />
}

// ─── Real Google Maps component ───────────────────────────────────────────────

interface GoogleMapProps {
  stops: Stop[]
  destinationName: string
  onStopClick?: (index: number) => void
}

function GoogleMapView({ stops, destinationName, onStopClick }: GoogleMapProps) {
  const mapDivRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<google.maps.Map | null>(null)
  const markersRef = useRef<google.maps.Marker[]>([])
  const [mapError, setMapError] = useState<string | null>(null)
  const [mapsLoaded, setMapsLoaded] = useState(false)

  const buildMap = useCallback(async () => {
    if (!mapDivRef.current || stops.length === 0) return

    try {
      // Resolve real coordinates for all stops
      const stopNames = stops.map((s) => s.name)
      const resolvedStops = await resolveStopCoordinates(stopNames)

      if (resolvedStops.length === 0) {
        setMapError('Could not locate stops on map')
        return
      }

      // Lazily fetch a thumbnail per stop (reused for InfoWindow photo popups)
      const imageResults = await Promise.all(
        stops.map((s) => fetchImages(`${s.name} ${destinationName}`, 1).catch(() => []))
      )

      const enrichedStops = resolvedStops.map((rs, i) => ({
        ...rs,
        imageUrl: imageResults[i]?.[0]?.thumb || imageResults[i]?.[0]?.url,
        timeLabel: stops[i]?.time,
      }))

      // Centre on first stop
      const centre = enrichedStops[0].position
      const map = await initMap(mapDivRef.current!, centre, 12)
      mapRef.current = map

      // Place markers — InfoWindow photo popup opens on click, sidebar sync preserved
      const markers = await placeMarkers(map, enrichedStops, onStopClick)
      markersRef.current = markers

      // Draw route line
      await drawRoute(map, enrichedStops)

      // Fit all stops in view
      await fitBounds(map, enrichedStops)

      setMapsLoaded(true)
    } catch (err) {
      console.warn('Google Maps render error:', err)
      setMapError(String(err))
    }
  }, [stops, destinationName, onStopClick])

  useEffect(() => {
    loadGoogleMaps()
      .then(() => buildMap())
      .catch((err) => setMapError(String(err)))

    return () => {
      // Cleanup markers on unmount
      markersRef.current.forEach((m) => m.setMap(null))
      markersRef.current = []
    }
  }, [buildMap])

  if (mapError) {
    return <FallbackMap stopCount={stops.length} />
  }

  return (
    <div className="relative w-full h-full">
      <div ref={mapDivRef} className="w-full h-full" style={{ minHeight: 250 }} />
      {!mapsLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-dusk-2">
          <div className="flex gap-1.5">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                className="w-2 h-2 rounded-full bg-sun"
                animate={{ y: [0, -7, 0] }}
                transition={{ duration: 1, repeat: Infinity, delay: i * 0.18 }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Stop photo thumbnail — fetches its own image, gracefully skips if none ────

function StopThumbnail({ stop, destination }: { stop: Stop; destination: Destination }) {
  const [image, setImage] = useState<Img | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const results = await fetchImages(`${stop.name} ${destination.name}`, 1)
      if (!cancelled) setImage(results[0] ?? null)
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stop.name, destination.name])

  if (!image?.url || failed) return null

  return (
    <div className="w-full h-16 rounded-lg overflow-hidden mt-1.5 mb-0.5 bg-dusk-2">
      <img
        src={image.thumb || image.url}
        alt={image.alt || stop.name}
        className="w-full h-full object-cover"
        loading="lazy"
        onError={() => setFailed(true)}
      />
    </div>
  )
}

// ─── Teardrop pin marker (SVG) ─────────────────────────────────────────────────

function TeardropPin({ index }: { index: number }) {
  return (
    <svg width="26" height="34" viewBox="0 0 26 34" className="flex-shrink-0 drop-shadow-[0_2px_6px_rgba(255,149,0,0.5)]">
      <path
        d="M13 0C5.8 0 0 5.8 0 13c0 8.5 10 18.5 12 20.5.5.5 1.5.5 2 0C16 31.5 26 21.5 26 13 26 5.8 20.2 0 13 0z"
        fill="#FF9500"
      />
      <circle cx="13" cy="13" r="9.5" fill="#FFFAF4" />
      <text x="13" y="17.5" textAnchor="middle" fontSize="11" fontWeight="800" fill="#FF6B2B" fontFamily="Nunito, sans-serif">
        {index + 1}
      </text>
    </svg>
  )
}

// ─── Journey Screen ───────────────────────────────────────────────────────────

export function JourneyScreen() {
  const { selectedDestination, dnaKey, leadEmail, setScreen } = useAppStore()
  const dnaProfile = DNA_PROFILES[dnaKey]
  const [activeStop, setActiveStop] = useState(0)

  const dest = selectedDestination
  if (!dest) { setScreen('dna'); return null }

  const handleUnlock = () => {
    // Returning users who already gave an email skip straight to the card.
    setScreen(leadEmail ? 'card' : 'gate')
  }

  return (
    <div className="yonder-world min-h-screen flex flex-col">
      <Atmosphere density="low" />
      <Topbar
        centerContent={<span className="text-white/70 font-bold">{dest.name} journey map</span>}
        backTo="dna"
      />

      {/* Header */}
      <div className="relative z-10 px-5 py-4 flex items-center justify-between bg-white/5 border-b border-white/10 backdrop-blur-sm">
        <div>
          <h2 className="text-xl font-black text-white tracking-tight">{dest.name}</h2>
          <p className="text-[11px] font-semibold text-white/60 mt-0.5">
            {dest.stops.length} family stops · {dest.ageRange}
          </p>
        </div>
        <div className="bg-sun text-white rounded-full text-[11px] font-black px-4 py-1.5 shadow-glow-sun">
          Save {dest.saving}
        </div>
      </div>

      {/* Map + stops panel */}
      <div
        className="relative z-10 grid border-b border-white/10"
        style={{ gridTemplateColumns: '1fr 220px', minHeight: 250 }}
      >
        {/* Map area — real Google Maps or fallback canvas */}
        <div className="border-r border-white/10 overflow-hidden" style={{ minHeight: 250 }}>
          <GoogleMapView
            stops={dest.stops}
            destinationName={dest.name}
            onStopClick={setActiveStop}
          />
        </div>

        {/* Stops panel */}
        <div className="overflow-y-auto bg-black/15">
          {dest.stops.map((stop: Stop, i: number) => (
            <button
              key={i}
              onClick={() => setActiveStop(i)}
              className={`w-full text-left px-3 py-2.5 border-b border-white/10 transition-colors ${
                activeStop === i
                  ? 'bg-sun/15 border-l-[3px] border-l-sun'
                  : 'hover:bg-white/5'
              }`}
            >
              <div className="flex items-center gap-1.5 mb-1">
                <TeardropPin index={i} />
                <div className="text-[9px] font-black text-sun-pale uppercase tracking-wide">
                  {stop.time}
                </div>
              </div>
              <div className="text-[11px] font-black text-white leading-tight">{stop.name}</div>
              <div className="text-[10px] font-semibold text-white/60 mt-1 leading-snug">
                {stop.description}
              </div>
              <StopThumbnail stop={stop} destination={dest} />
              <div className="text-[9px] font-black text-aqua mt-1">{stop.familyTag}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Partner deals */}
      <div className="relative z-10 px-5 pt-3 pb-1">
        <div className="flex items-center justify-between mb-2.5">
          <span className="text-[10px] font-black tracking-widest text-sun-pale/80 uppercase">
            🎁 Partner deals · unlocked for your family
          </span>
          <span className="bg-grass text-white rounded-full text-[9px] font-black px-2.5 py-1">
            Live pricing
          </span>
        </div>
        <div className="flex flex-col gap-2 mb-3">
          {dest.deals.map((deal: Deal, i: number) => (
            <motion.button
              key={i}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.08 }}
              onClick={() => openDeal(deal, dest.name, dnaProfile?.name ?? '')}
              className="flex items-center gap-3 yonder-cream-card rounded-2xl px-3 py-2.5 shadow-lg hover:shadow-glow-sun active:scale-[0.98] transition-all text-left w-full group border-2 border-transparent hover:border-sun"
              title={`Book via ${getNetworkLabel(deal.network)}`}
            >
              <div className="w-9 h-9 rounded-xl bg-sand flex items-center justify-center text-lg flex-shrink-0">
                {deal.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-black text-ink group-hover:text-sun transition-colors">{deal.title}</div>
                <div className="text-[10px] font-semibold text-ink-3 mt-0.5">{deal.detail}</div>
                <div
                  className="inline-block text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded mt-1"
                  style={{
                    background: NETWORK_COLORS[deal.network] + '18',
                    color: NETWORK_COLORS[deal.network],
                  }}
                >
                  via {getNetworkLabel(deal.network)}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                <div className="bg-sun-pale border-2 border-sun-light rounded-full text-[9px] font-black px-2.5 py-1 text-ink whitespace-nowrap">
                  {deal.saving}
                </div>
                <div className="text-[9px] font-black text-sun opacity-0 group-hover:opacity-100 transition-opacity">
                  Book now →
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {/* CTA bar */}
      <div className="relative z-10 sticky bottom-0 px-4 py-3 bg-black/30 border-t border-white/10 backdrop-blur-sm grid grid-cols-[1fr_1.8fr] gap-2">
        <button
          onClick={() => setScreen('dna')}
          className="bg-white/10 text-white/80 font-bold text-sm rounded-2xl py-3 hover:bg-white/15 transition-colors"
        >
          ← Back
        </button>
        <motion.button
          onClick={handleUnlock}
          className="bg-gradient-to-r from-sun to-sun-light text-white font-black text-sm rounded-2xl py-3 shadow-glow-sun hover:shadow-sun-lg transition-shadow"
          whileTap={{ scale: 0.98 }}
        >
          Unlock our family itinerary →
        </motion.button>
      </div>
    </div>
  )
}
